import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFolderStore } from '@/store/folderStore';
import { useCompose } from '@/contexts/ComposeContext';
import { useSidebar } from '@/contexts/SidebarContext';

import {
  Star,
  Mail,
  Send,
  FileText,
  AlertCircle,
  Trash2,
  User,

  Folder,
} from 'lucide-react';
import AddFolderModal from '@/components/email/AddFolderModal';
import Tooltip from '@/components/ui/Tooltip';
import CountBadge from '@/components/ui/CountBadge';
import './Sidebar.css';

const folderIcons: Record<string, React.ReactNode> = {
  INBOX: <Mail size={20} />,
  Inbox: <Mail size={20} />,
  Starred: <Star size={20} />,
  Sent: <Send size={20} />,
  Drafts: <FileText size={20} />,
  Trash: <Trash2 size={20} />,
  Spam: <AlertCircle size={20} />,
};

// const chats = [
//   // { label: 'Start New Chat', avatar: 'https://placehold.co/32x32/0066ff/white?text=+', start: true },
//   // { label: 'Jel Chibuzo', avatar: 'https://placehold.co/32x32/4f46e5/white?text=JC', online: true },
//   // { label: 'Larry Offiong', avatar: 'https://placehold.co/32x32/059669/white?text=LO', online: true },
//   // { label: 'Agnes Parsons', avatar: 'https://placehold.co/32x32/dc2626/white?text=AP', badge: 3 },
// ];

const Sidebar: React.FC = () => {
  const { sidebarOpen, setSidebarOpen } = useSidebar();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [customFoldersCollapsed, setCustomFoldersCollapsed] = useState(false);
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  const { folders, currentFolder, setCurrentFolder, fetchFolders } = useFolderStore();
  const { openCompose } = useCompose();
  const navigate = useNavigate();


  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check initial size

    return () => window.removeEventListener('resize', handleResize);
  }, [setSidebarOpen]);

  // Mobile sidebar toggle
  const handleHamburger = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Toggle custom folders section
  const handleCustomFoldersToggle = () => {
    setCustomFoldersCollapsed(prev => !prev);
  };

  // Handle folder selection
  const handleFolderClick = (folderName: string) => {
    setCurrentFolder(folderName);
              
    // Map folder names to routes
    const folderRoutes: { [key: string]: string } = {
      'INBOX': '/inbox',
      'Sent': '/sent',
      'Starred': '/starred',
      'Drafts': '/drafts',
      'Archive': '/archive',
      'Spam': '/spam',
      'Trash': '/trash',
      'Junk': '/junk'
    };
              
    // Navigate to the appropriate route
    const route = folderRoutes[folderName];
    const baseRoute = route || `/folder/${folderName}`;

    // Navigate to the new folder's route
    navigate(baseRoute);

    // Close sidebar on mobile after navigation
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Handle send mail (stub)
  // const handleSendMail = (data: { to: string; subject: string; body: string; attachments: File[] }) => {
  //   // TODO: Implement send mail API
  //   console.log('Send mail:', data);
  // };



  const sidebarClassName = [
    'app-sidebar',
    !sidebarOpen && 'app-sidebar--collapsed',
    isMobile && 'app-sidebar--mobile',
    isMobile && !sidebarOpen && 'app-sidebar--hidden',
  ].filter(Boolean).join(' ');

  return (
    <>
      {isMobile && sidebarOpen && (
        <div className="app-sidebar__overlay" onClick={() => setSidebarOpen(false)} />
      )}
      <aside className={sidebarClassName}>
        <div className="app-sidebar__header">
          <button
            type="button"
            className="app-sidebar__menu-btn"
            onClick={handleHamburger}
            title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          {sidebarOpen && (
            <div className="app-sidebar__logo">
              <img src="/logos/logo.png" alt="Cross River" />
            </div>
          )}
        </div>

        <button
          type="button"
          className="app-sidebar__compose"
          onClick={(e) => {
            e.preventDefault();
            openCompose();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          {sidebarOpen && 'Write Message'}
        </button>

        <div className="app-sidebar__scroll scrollable-sidebar">
          {folders.filter((folder) => folder.type === 'system').length === 0 ? (
            <div className="app-sidebar__empty">No system folders found.</div>
          ) : (
            folders
              .filter((folder) => folder.type === 'system')
              .map((folder) => {
                const icon = folderIcons[folder.name] || <Mail size={20} />;
                const unseenCount = folder.unseen > 0 ? folder.unseen : 0;
                const displayName =
                  folder.name === 'INBOX'
                    ? 'Inbox'
                    : folder.name.charAt(0).toUpperCase() + folder.name.slice(1).toLowerCase();
                const isActive = currentFolder === folder.name;

                const item = (
                  <div
                    key={folder.name}
                    className={`app-sidebar__nav-item${isActive ? ' app-sidebar__nav-item--active' : ''}`}
                    onClick={() => handleFolderClick(folder.name)}
                    title={!sidebarOpen ? displayName : undefined}
                  >
                    <span className="app-sidebar__nav-icon" style={{ position: 'relative' }}>
                      {icon}
                      {!sidebarOpen && <CountBadge count={unseenCount} variant="icon" />}
                    </span>
                    {sidebarOpen && (
                      <>
                        <span className="app-sidebar__nav-label">{displayName}</span>
                        <CountBadge count={unseenCount} variant="inline" />
                      </>
                    )}
                  </div>
                );

                return !sidebarOpen ? <Tooltip key={folder.name} text={displayName}>{item}</Tooltip> : item;
              })
          )}

          {sidebarOpen ? (
            <button type="button" className="app-sidebar__section-title" onClick={handleCustomFoldersToggle}>
              My Folders
              <svg
                className={`app-sidebar__section-chevron${customFoldersCollapsed ? ' app-sidebar__section-chevron--collapsed' : ''}`}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="6,9 12,15 18,9" />
              </svg>
            </button>
          ) : (
            <div className="app-sidebar__divider" />
          )}

          {!customFoldersCollapsed && (
            <>
              {folders.filter((folder) => folder.type === 'custom').length === 0 ? (
                sidebarOpen && <div className="app-sidebar__empty">No custom folders found</div>
              ) : (
                folders
                  .filter((folder) => folder.type === 'custom')
                  .map((folder) => {
                    const unseenCount = folder.unseen > 0 ? folder.unseen : 0;
                    const isActive = currentFolder === folder.name;

                    const item = (
                      <div
                        key={folder.name}
                        className={`app-sidebar__custom-item${isActive ? ' app-sidebar__custom-item--active' : ''}`}
                        onClick={() => handleFolderClick(folder.name)}
                      >
                        <span style={{ position: 'relative', display: 'flex' }}>
                          <Folder size={18} />
                          {!sidebarOpen && <CountBadge count={unseenCount} variant="icon" />}
                        </span>
                        {sidebarOpen && (
                          <>
                            <span className="app-sidebar__nav-label">{folder.name}</span>
                            <CountBadge count={unseenCount} variant="inline" />
                          </>
                        )}
                      </div>
                    );

                    return !sidebarOpen ? <Tooltip key={folder.name} text={folder.name}>{item}</Tooltip> : item;
                  })
              )}
            </>
          )}
        </div>

        <div className="app-sidebar__footer">
          <div
            className="app-sidebar__account"
            onClick={() => navigate('/profile')}
            title={!sidebarOpen ? 'My Account' : undefined}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/profile')}
          >
            <User size={20} />
            {sidebarOpen && <span>My Account</span>}
          </div>
        </div>

        <div className="app-sidebar__brand">
          {sidebarOpen ? (
            <a href="https://theqloop.com" target="_blank" rel="noopener noreferrer">
              Powered by
              <img src="/logos/logo-qloop.png" alt="Qloop" />
            </a>
          ) : (
            <a href="https://theqloop.com" target="_blank" rel="noopener noreferrer">
              <img src="/logos/logo-qloop.png" alt="Qloop" />
            </a>
          )}
        </div>
      </aside>

      {/* Add Folder Modal */}
      <AddFolderModal
        isOpen={isAddFolderModalOpen}
        onClose={() => setIsAddFolderModalOpen(false)}
        onFolderAdded={fetchFolders}
      />
    </>
  );
};

export default Sidebar;
